import { useState } from 'react';
import { Button, Group, MultiSelect, TextInput } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import { IconSearch } from '@tabler/icons-react';
import {
  HighwayMaintenanceFeePenaltyTicketState,
  HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_STATE_POLICY,
  HIGHWAY_MAINTENANCE_FEE_PENALTY_STATE_VALUE,
} from '#/components/ui/tickets/highway-maintenance-fee-penalty/model';
import type { DefaultOptionsParams } from './useTicketSearchBarParams';
import { useTicketSearchBarParams } from './useTicketSearchBarParams';

type YYYYMMDD = string;

const getStatesOption = (
  availStates: HighwayMaintenanceFeePenaltyTicketState[] = HIGHWAY_MAINTENANCE_FEE_PENALTY_STATE_VALUE,
) => {
  return Object.entries(HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKET_STATE_POLICY)
    .filter(([key]) =>
      availStates.includes(key as HighwayMaintenanceFeePenaltyTicketState),
    )
    .map(([value, { text }]) => ({
      value: value as HighwayMaintenanceFeePenaltyTicketState,
      label: text,
    }));
};

const getMonthRangeFromDate = (date: Date): [YYYYMMDD, YYYYMMDD] => [
  format(startOfMonth(date), 'yyyy-MM-dd'),
  format(endOfMonth(date), 'yyyy-MM-dd'),
];

const dateFromYYYYMMDD = (s: string | undefined): Date | null =>
  s ? parseISO(s) : null;

export type AvailTicketSearchBarField = {
  dateRange?: boolean;
  state?: boolean;
  search?: boolean;
};

export type HighwayMaintenanceFeePenaltyTicketSearchBarProps = {
  defaultOptions: DefaultOptionsParams;
  availField?: AvailTicketSearchBarField;
  availStates?: HighwayMaintenanceFeePenaltyTicketState[];
};

export function HighwayMaintenanceFeePenaltyTicketSearchBar({
  defaultOptions,
  availField,
  availStates,
}: HighwayMaintenanceFeePenaltyTicketSearchBarProps) {
  const { params, setState, setFuzzySearchText, reset, setRange } =
    useTicketSearchBarParams(defaultOptions);
  const [inputSearchValue, setSearchInputValue] = useState(
    () => params.fuzzySearchText ?? '',
  );

  const statesOptions = getStatesOption(availStates);

  return (
    <Group gap="sm">
      {availField?.search !== false && (
        <TextInput
          flex={1}
          value={inputSearchValue}
          onChange={(e) => {
            setFuzzySearchText(e.target.value);
            setSearchInputValue(e.target.value);
          }}
          type="text"
          placeholder="搜尋 Email、車牌、證件號碼 / 統編"
          leftSection={<IconSearch size={16} />}
        />
      )}
      {availField?.state !== false && (
        <MultiSelect
          w={160}
          data={statesOptions}
          placeholder={params.state.length < 1 ? '全部' : undefined}
          clearable
          value={params.state ?? undefined}
          renderPill={({ option }) => {
            if (option.value !== params.state[0]) return null;
            if (params.state.length > 1) return '...';
            return option.label;
          }}
          onChange={(v) => setState(v)}
        />
      )}
      {availField?.dateRange !== false && (
        <MonthPickerInput
          valueFormat="YYYY-MM"
          placeholder="選擇月份"
          value={dateFromYYYYMMDD(params.fromDate)}
          onChange={(v) => {
            if (!v) {
              setRange(undefined);
            } else {
              const [from, to] = getMonthRangeFromDate(new Date(v));
              setRange([from, to]);
            }
          }}
          clearable
          w={110}
        />
      )}
      <Button variant="default" onClick={reset}>
        重設
      </Button>
    </Group>
  );
}
